import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { userAPI, authAPI, projectAPI, threadAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Github, Calendar, Edit, Save, X, FolderOpen, MessageSquare, Loader2 } from 'lucide-react';

export default function Profile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', bio: '', github_url: '' });
  const [isSaving, setIsSaving] = useState(false);

  const isOwnProfile = !userId || userId === currentUser?.user_id;

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Get current user
      let current = null;
      try {
        const currentRes = await authAPI.getMe();
        current = currentRes.data;
        setCurrentUser(current);
      } catch (e) {
        // Not logged in
      }

      // Get profile to display
      let profileToShow;
      if (userId) {
        const profileRes = await userAPI.getProfile(userId);
        profileToShow = profileRes.data;
      } else if (current) {
        profileToShow = current;
      }
      
      setProfile(profileToShow);
      setEditForm({
        name: profileToShow?.name || '',
        bio: profileToShow?.bio || '',
        github_url: profileToShow?.github_url || '',
      });

      // Get user's projects and posts
      if (profileToShow) {
        const [projectsRes, threadsRes] = await Promise.all([
          projectAPI.getAll({}),
          threadAPI.getAll({}),
        ]);
        setUserProjects(projectsRes.data.filter(p => p.author_id === profileToShow.user_id));
        setUserPosts(threadsRes.data.filter(p => p.author_id === profileToShow.user_id));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await userAPI.updateProfile(editForm);
      setProfile(response.data);
      setIsEditing(false);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-slate-600">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={profile.picture} />
                <AvatarFallback className="text-3xl bg-indigo-100 text-indigo-700">
                  {profile.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your name"
                        data-testid="edit-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={editForm.bio}
                        onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                        placeholder="Tell us about yourself..."
                        className="min-h-[80px]"
                        data-testid="edit-bio-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>GitHub URL</Label>
                      <Input
                        value={editForm.github_url}
                        onChange={(e) => setEditForm(prev => ({ ...prev, github_url: e.target.value }))}
                        placeholder="https://github.com/username"
                        data-testid="edit-github-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={isSaving} className="bg-indigo-600" data-testid="save-profile-btn">
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)}>
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h1 className="text-2xl font-bold text-slate-900" data-testid="profile-name">
                          {profile.name}
                        </h1>
                        <p className="text-slate-600">{profile.email}</p>
                      </div>
                      {isOwnProfile && (
                        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                      )}
                    </div>

                    {profile.bio && (
                      <p className="text-slate-700 mb-4" data-testid="profile-bio">{profile.bio}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                      {profile.github_url && (
                        <a
                          href={profile.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-slate-900"
                          data-testid="profile-github-link"
                        >
                          <Github className="w-4 h-4" />
                          GitHub
                        </a>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Joined {profile.created_at ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: true }) : 'recently'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User's Content */}
        <Tabs defaultValue="projects">
          <TabsList className="mb-6">
            <TabsTrigger value="projects" className="gap-2">
              <FolderOpen className="w-4 h-4" />
              Projects ({userProjects.length})
            </TabsTrigger>
            <TabsTrigger value="posts" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Posts ({userPosts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {userProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No projects yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {userProjects.map((project) => (
                  <Link key={project.project_id} to={`/projects/${project.project_id}`}>
                    <Card className="card-hover h-full">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{project.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{project.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>↑ {project.upvotes - project.downvotes}</span>
                          <span>{project.comment_count} comments</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="posts">
            {userPosts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No posts yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {userPosts.map((post) => (
                  <Link key={post.post_id} to={`/forum/${post.post_id}`}>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{post.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{post.content}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>↑ {post.upvotes - post.downvotes}</span>
                          <span>{post.comment_count} comments</span>
                          <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
